import React, { useMemo, useState, useEffect, useRef } from 'react';
import './WorldDbFlowFilter.css';

const STEP_LABELS = {
  meet_in_person: "Meet In Person", sales_meeting: "Sales Meeting", online_meeting: "Online Meeting",
  phone_call: "Phone Call", discovery_call: "Discovery Call", follow_up_call: "Follow Up Call",
  demo_given: "Demo Given", proposal_sent: "Proposal Sent", quote_sent: "Quote Sent",
  contract_sent: "Contract Sent", contract_signed: "Contract Signed", invoice_sent: "Invoice Sent",
  payment_received: "Payment Received", onboarding: "Onboarding", email_sent: "Send Email",
  instagram_follow: "Follow", instagram_like: "Like Post", instagram_comment: "Comment on Post",
  instagram_dm: "Send DM", instagram_voice: "Voice DM", instagram_story_reply: "Reply to Story",
  instagram_story_mention: "Mention in Story", instagram_reel_comment: "Comment on Reel",
  linkedin_connect: "Connection Request", linkedin_message: "Send Message", linkedin_like: "Like Post",
  linkedin_comment: "Comment on Post", linkedin_endorse: "Endorse Skills",
  tiktok_follow: "Follow", tiktok_like: "Like Video", tiktok_comment: "Comment on Video", tiktok_dm: "Send DM",
  twitter_follow: "Follow", twitter_like: "Like Tweet", twitter_retweet: "Retweet",
  twitter_reply: "Reply to Tweet", twitter_dm: "Send DM",
  facebook_like: "Like Page", facebook_comment: "Comment on Post", facebook_message: "Send Message",
  lead_magnet: "Lead Magnet", ebook_sent: "eBook Sent", whitepaper_sent: "Whitepaper Sent",
  case_study_sent: "Case Study Sent", webinar: "Webinar Invite", workshop_invite: "Workshop Invite",
  free_trial: "Free Trial Offered", discount_offer: "Discount Offer",
  follow_up: "Follow Up", research_lead: "Research Lead", update_crm: "Update CRM",
  assign_to_rep: "Assign to Rep", add_note: "Add Note", add_tag: "Add Tag",
  move_stage: "Move Stage", create_task: "Create Task",
  delay: "Wait / Delay", condition: "Condition", end: "End", tag: "Add Tags", split: "A/B Split",
};

const STEP_FA_ICONS = {
  email_sent: 'fa-solid fa-envelope',
  phone_call: 'fa-solid fa-phone', discovery_call: 'fa-solid fa-phone', follow_up_call: 'fa-solid fa-phone',
  meet_in_person: 'fa-solid fa-handshake', sales_meeting: 'fa-solid fa-handshake', online_meeting: 'fa-solid fa-video',
  demo_given: 'fa-solid fa-desktop', proposal_sent: 'fa-solid fa-file-lines', quote_sent: 'fa-solid fa-file-invoice-dollar',
  contract_sent: 'fa-solid fa-file-signature', contract_signed: 'fa-solid fa-file-contract',
  invoice_sent: 'fa-solid fa-file-invoice', payment_received: 'fa-solid fa-money-bill-wave', onboarding: 'fa-solid fa-rocket',
  instagram_follow: 'fa-brands fa-instagram', instagram_like: 'fa-brands fa-instagram',
  instagram_comment: 'fa-brands fa-instagram', instagram_dm: 'fa-brands fa-instagram',
  instagram_voice: 'fa-brands fa-instagram', instagram_story_reply: 'fa-brands fa-instagram',
  instagram_story_mention: 'fa-brands fa-instagram', instagram_reel_comment: 'fa-brands fa-instagram',
  linkedin_connect: 'fa-brands fa-linkedin', linkedin_message: 'fa-brands fa-linkedin',
  linkedin_like: 'fa-brands fa-linkedin', linkedin_comment: 'fa-brands fa-linkedin', linkedin_endorse: 'fa-brands fa-linkedin',
  tiktok_follow: 'fa-brands fa-tiktok', tiktok_like: 'fa-brands fa-tiktok',
  tiktok_comment: 'fa-brands fa-tiktok', tiktok_dm: 'fa-brands fa-tiktok',
  twitter_follow: 'fa-brands fa-x-twitter', twitter_like: 'fa-brands fa-x-twitter',
  twitter_retweet: 'fa-brands fa-x-twitter', twitter_reply: 'fa-brands fa-x-twitter', twitter_dm: 'fa-brands fa-x-twitter',
  facebook_like: 'fa-brands fa-facebook', facebook_comment: 'fa-brands fa-facebook', facebook_message: 'fa-brands fa-facebook',
  delay: 'fa-solid fa-clock', condition: 'fa-solid fa-code-branch', end: 'fa-solid fa-flag-checkered',
  tag: 'fa-solid fa-tags', split: 'fa-solid fa-random',
  follow_up: 'fa-solid fa-redo', research_lead: 'fa-solid fa-search',
};

const getStepLabel = (type) => {
  if (!type) return 'Step';
  return STEP_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getStepIcon = (type) => STEP_FA_ICONS[type] || 'fa-solid fa-circle-dot';

const getOrderedNodes = (nodes, edges) => {
  if (!nodes || !edges || nodes.length === 0) return [];
  const triggerNode = nodes.find(n => n.type === 'trigger');
  if (!triggerNode) return nodes.filter(n => n.type !== 'trigger');
  const orderedNodes = [];
  const visited = new Set();
  const queue = [triggerNode.id];
  while (queue.length > 0) {
    const currentId = queue.shift();
    if (visited.has(currentId)) continue;
    visited.add(currentId);
    const currentNode = nodes.find(n => n.id === currentId);
    if (currentNode && currentNode.type !== 'trigger') orderedNodes.push(currentNode);
    edges.filter(e => e.source === currentId).forEach(edge => {
      if (!visited.has(edge.target)) queue.push(edge.target);
    });
  }
  return orderedNodes;
};

const WorldDbFlowFilter = ({
  warmupFlows = [],
  onFilterChange,
  onStepFilterChange,
  currentFlowFilter = 'all',
  currentStepFilter = 'all',
  isLoading
}) => {
  const [flowOpen, setFlowOpen] = useState(false);
  const [stepOpen, setStepOpen] = useState(false);
  const flowRef = useRef(null);
  const stepRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (flowRef.current && !flowRef.current.contains(e.target)) setFlowOpen(false);
      if (stepRef.current && !stepRef.current.contains(e.target)) setStepOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleFlowSelect = (value) => {
    onFilterChange(value);
    if (onStepFilterChange) onStepFilterChange('all');
    setFlowOpen(false);
  };

  const handleStepSelect = (value) => {
    if (onStepFilterChange) onStepFilterChange(value);
    setStepOpen(false);
  };

  const isAll = !currentFlowFilter || currentFlowFilter === 'all';
  const selectedFlow = useMemo(() => {
    if (isAll || currentFlowFilter === 'no_flow' || currentFlowFilter === 'has_flow' || currentFlowFilter === 'finished') return null;
    return warmupFlows.find(f => f.id === currentFlowFilter);
  }, [currentFlowFilter, warmupFlows, isAll]);

  const currentFlowLabel = isAll
    ? 'All Flows'
    : currentFlowFilter === 'no_flow' ? 'No Flow'
    : currentFlowFilter === 'has_flow' ? 'In Any Flow'
    : currentFlowFilter === 'finished' ? 'Finished'
    : (selectedFlow?.name || currentFlowFilter);

  const stepOptions = useMemo(() => {
    if (!selectedFlow?.steps) return [];
    const buildSteps = (stepsArray, orderedNodes) => {
      let hasEnd = false;
      const items = [];
      let displayCount = 0;
      const source = orderedNodes || stepsArray;
      source.forEach((nodeOrStep, idx) => {
        const step = orderedNodes
          ? stepsArray.find(s => s.id === nodeOrStep.id) || null
          : nodeOrStep;
        const stepType = step?.type || nodeOrStep?.data?.actionType || 'Step';
        if (stepType === 'end') { hasEnd = true; return; }
        displayCount++;
        const originalIndex = orderedNodes
          ? stepsArray.findIndex(s => s.id === nodeOrStep.id)
          : idx;
        items.push({
          value: String(originalIndex >= 0 ? originalIndex : displayCount - 1),
          label: `${displayCount}. ${getStepLabel(stepType)}`,
          type: stepType,
        });
      });
      if (hasEnd) items.push({ value: 'flow_ended', label: 'Flow Ended', type: 'end' });
      return [{ value: 'all', label: 'All Steps', type: null }, ...items];
    };
    if (selectedFlow.visualData?.nodes && selectedFlow.visualData?.edges) {
      return buildSteps(selectedFlow.steps, getOrderedNodes(selectedFlow.visualData.nodes, selectedFlow.visualData.edges));
    }
    return buildSteps(selectedFlow.steps, null);
  }, [selectedFlow]);

  const showStepFilter = selectedFlow && stepOptions.length > 1;
  const currentStepLabel = stepOptions.find(s => s.value === currentStepFilter)?.label || 'All Steps';

  return (
    <div className="wdff-wrapper">
      {/* Flow Dropdown */}
      <div className="wdff-filter" ref={flowRef}>
        <button
          className={`wdff-trigger ${flowOpen ? 'wdff-trigger-open' : ''} ${!isAll ? 'wdff-trigger-active' : ''}`}
          onClick={() => !isLoading && warmupFlows.length > 0 && setFlowOpen(p => !p)}
          disabled={isLoading || warmupFlows.length === 0}
          title="Filter by flow"
        >
          <i className="fa-solid fa-fire wdff-trigger-icon" />
          <span className="wdff-trigger-label">{currentFlowLabel}</span>
          <svg className={`wdff-trigger-chevron ${flowOpen ? 'wdff-trigger-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {flowOpen && (
          <div className="wdff-dropdown">
            {[
              { value: 'all', label: 'All Flows', fa: 'fa-solid fa-border-all', desc: 'Show all contacts' },
              { value: 'no_flow', label: 'No Flow', fa: 'fa-solid fa-circle-minus', desc: 'Not in any flow' },
              { value: 'has_flow', label: 'In Any Flow', fa: 'fa-solid fa-circle-play', desc: 'Active in a flow' },
              { value: 'finished', label: 'Finished', fa: 'fa-solid fa-flag-checkered', desc: 'Completed a flow (FIN)' },
            ].map(opt => (
              <div
                key={opt.value}
                className={`wdff-option ${currentFlowFilter === opt.value ? 'wdff-option-selected' : ''}`}
                onClick={() => handleFlowSelect(opt.value)}
              >
                <span className={`wdff-radio ${currentFlowFilter === opt.value ? 'wdff-radio-active' : ''}`} />
                <i className={`${opt.fa} wdff-option-fa`} />
                <div className="wdff-option-content">
                  <span className="wdff-option-label">{opt.label}</span>
                  <span className="wdff-option-desc">{opt.desc}</span>
                </div>
              </div>
            ))}

            {warmupFlows.length > 0 && <div className="wdff-divider" />}
            {warmupFlows.length > 0 && (
              <div className="wdff-group-label">Your Flows</div>
            )}

            <div className="wdff-list">
              {warmupFlows.map(flow => {
                const active = flow.id === currentFlowFilter;
                const stepCount = flow.steps ? flow.steps.filter(s => s.type !== 'end').length : 0;
                return (
                  <div
                    key={flow.id}
                    className={`wdff-option ${active ? 'wdff-option-selected' : ''}`}
                    onClick={() => handleFlowSelect(flow.id)}
                  >
                    <span className={`wdff-radio ${active ? 'wdff-radio-active' : ''}`} />
                    <i className="fa-solid fa-fire wdff-option-fa wdff-option-fa-flow" />
                    <div className="wdff-option-content">
                      <span className="wdff-option-label">{flow.name}</span>
                      <div className="wdff-option-meta">
                        {stepCount > 0 && (
                          <span className="wdff-meta-badge wdff-meta-steps">
                            {stepCount} {stepCount === 1 ? 'step' : 'steps'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Step Dropdown */}
      {showStepFilter && (
        <div className="wdff-filter" ref={stepRef}>
          <button
            className={`wdff-trigger wdff-trigger-step ${stepOpen ? 'wdff-trigger-open' : ''} ${currentStepFilter !== 'all' ? 'wdff-trigger-active' : ''}`}
            onClick={() => !isLoading && setStepOpen(p => !p)}
            disabled={isLoading}
            title="Filter by step"
          >
            <i className="fa-solid fa-shoe-prints wdff-trigger-icon" />
            <span className="wdff-trigger-label">{currentStepLabel}</span>
            <svg className={`wdff-trigger-chevron ${stepOpen ? 'wdff-trigger-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {stepOpen && (
            <div className="wdff-dropdown">
              <div className="wdff-list">
                {stepOptions.map(opt => {
                  const active = opt.value === currentStepFilter;
                  return (
                    <div
                      key={opt.value}
                      className={`wdff-option ${active ? 'wdff-option-selected' : ''}`}
                      onClick={() => handleStepSelect(opt.value)}
                    >
                      <span className={`wdff-radio ${active ? 'wdff-radio-active' : ''}`} />
                      <i className={`${opt.type ? getStepIcon(opt.type) : 'fa-solid fa-border-all'} wdff-option-fa`} />
                      <span className="wdff-option-label">{opt.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorldDbFlowFilter;
